/* global Office */
import { TextEncoder } from 'text-decoding'
import { computed, ref, watch } from 'vue'
import { useNotify } from './composables/notify'

if (!window.TextDecoder) {
  window.TextEncoder = TextEncoder
}

class Outlook {
  setAttendeesBroken = null
  notify = useNotify()

  getUserProfile () {
    if (Office.context.requirements.isSetSupported('Mailbox', '1.6')) {
      // Mailbox 1.6
      return Office.context.mailbox.userProfile
    }
    return null
  }

  useAppointmentSchedule () {
    const start = ref()
    const end = ref()
    const recurrence = ref(null)

    const isAllDay = computed(() => {
      return start.value.getTime() === new Date(start.value).setHours(0, 0, 0, 0) &&
        end.value.getTime() === new Date(end.value).setHours(0, 0, 0, 0)
    })

    const isToday = computed(() => {
      return start.value?.getTime() >= new Date().setHours(0, 0, 0, 0) &&
        end.value?.getTime() <= new Date().setHours(23, 59, 59, 999)
    })

    let recurrenceInterval

    const getTime = () => {
      Office.context.mailbox.item.start.getAsync(result => {
        if (result.value.getTime() !== start.value?.getTime()) {
          start.value = result.value
        }
      })

      Office.context.mailbox.item.end.getAsync(result => {
        if (result.value.getTime() !== end.value?.getTime()) {
          end.value = result.value
        }
      })
    }

    getTime()

    if (Office.context.requirements.isSetSupported('Mailbox', '1.7')) {
      // Mailbox 1.7
      const getRecurrence = () => new Promise((resolve) => Office.context.mailbox.item.recurrence.getAsync(result => {
        resolve(result.value)
      }))

      getRecurrence().then(value => {
        recurrence.value = value
      })

      // Mailbox 1.7
      Office.context.mailbox.item.addHandlerAsync(Office.EventType.RecurrenceChanged, args => {
        recurrence.value = args.recurrence
      })

      watch(recurrence, value => {
        if (value && !recurrenceInterval) {
          recurrenceInterval = setInterval(async () => {
            recurrence.value = await getRecurrence()
          }, 10000)
        } else if (!value && recurrenceInterval) {
          clearInterval(recurrenceInterval)
          recurrenceInterval = undefined
        }
      }, { immediate: true })
    }

    if (Office.context.requirements.isSetSupported('Mailbox', '1.7')) {
      // Mailbox 1.7
      Office.context.mailbox.item.addHandlerAsync(Office.EventType.AppointmentTimeChanged, args => {
        start.value = new Date(args.start)
        end.value = new Date(args.end)
      })
    } else {
      setInterval(() => {
        getTime()
      }, 10000)
    }

    return {
      start,
      end,
      recurrence,
      isAllDay,
      isToday,
    }
  }

  useRequiredAttendees () {
    const attendees = ref([])

    const get = () => Office.context.mailbox.item.requiredAttendees.getAsync(result => {
      if (JSON.stringify(attendees.value) !== JSON.stringify(result.value)) {
        attendees.value = result.value
      }
    })

    if (Office.context.requirements.isSetSupported('Mailbox', '1.7')) {
      // Mailbox 1.7
      Office.context.mailbox.item.addHandlerAsync(Office.EventType.RecipientsChanged, args => {
        if (args.changedRecipientFields.requiredAttendees) {
          get()
        }
      })
    } else {
      setInterval(() => {
        get()
      }, 10000)
    }

    get()

    return computed(() => attendees.value.map(attendee => attendee.emailAddress))
  }

  useLocations () {
    const locations = ref([])

    if (Office.context.requirements.isSetSupported('Mailbox', '1.8')) {
      // Mailbox 1.8
      const get = () => Office.context.mailbox.item.enhancedLocation.getAsync(result => {
        // Outlook 365 returns null if the location is empty
        const value = result.value || []
        if (JSON.stringify(locations.value) !== JSON.stringify(value)) {
          locations.value = value
        }
      })
      // Mailbox 1.7
      Office.context.mailbox.item.addHandlerAsync(Office.EventType.EnhancedLocationsChanged, get)
      get()
    }

    return computed(() => locations.value
      .filter(location => !!location.emailAddress)
      .map(location => location.emailAddress))
  }

  removeLocation (email) {
    if (Office.context.requirements.isSetSupported('Mailbox', '1.8')) {
      // Mailbox 1.8
      Office.context.mailbox.item.enhancedLocation.removeAsync([
        {
          id: email,
          type: Office.MailboxEnums.LocationType.Room,
        },
      ])
    }
  }

  recipientsChanged (callback) {
    window.Office.context.ui.addHandlerAsync(
      window.Office.EventType.DialogParentMessageReceived,
      callback,
    )
  }

  messageParent (message) {
    window.Office.context.ui.messageParent(message)
  }

  addAttendee (email) {
    Office.context.mailbox.item.requiredAttendees.addAsync([email])
  }

  removeAttendee (email) {
    Office.context.mailbox.item.requiredAttendees.getAsync(result => {
      const attendees = result.value.filter(a => a.emailAddress !== email)

      if (this.setAttendeesBroken) {
        attendees.splice(attendees.find(a => a.emailAddress !== this.setAttendeesBroken), 1)
      }

      Office.context.mailbox.item.requiredAttendees.setAsync(attendees, () => {
        if (this.setAttendeesBroken !== null) {
          return
        }

        Office.context.mailbox.item.requiredAttendees.getAsync(result => {
          if (result.value.length === attendees.length) {
            this.setAttendeesBroken = false
            return
          }
          if (Office.context.requirements.isSetSupported('Mailbox', '1.7')) {
            // Mailbox 1.7
            Office.context.mailbox.item.organizer.getAsync(organiser => {
              this.setAttendeesBroken = organiser.value.emailAddress
              attendees.splice(attendees.find(a => a.emailAddress !== this.setAttendeesBroken), 1)
              Office.context.mailbox.item.requiredAttendees.setAsync(attendees)
            })
          }
        })
      })
    })
  }

  getSetting (name) {
    return Office.context.roamingSettings.get(name)
  }

  setSetting (name, value) {
    Office.context.roamingSettings.set(name, value)
  }

  saveSettings () {
    return new Promise(resolve => {
      Office.context.roamingSettings.saveAsync(() => resolve())
    })
  }

  displayDialog (url, messageHandler) {
    Office.context.ui.displayDialogAsync(url, result => {
      result.value.addEventHandler(Office.EventType.DialogMessageReceived, event => {
        messageHandler(event.message, result.value)
      })
    })
  }

  get language () {
    const lang = Office.context.displayLanguage.split('-')
    return lang.length > 0 ? lang[0] : 'en'
  }

  async getCustomProperty (name) {
    return await new Promise(resolve => {
      Office.context.mailbox.item.loadCustomPropertiesAsync(result => {
        resolve(result.value.get(name))
      })
    })
  }

  async setCustomProperty (key, value) {
    return await new Promise(resolve => {
      Office.context.mailbox.item.loadCustomPropertiesAsync(result => {
        result.value.set(key, value)
        result.value.saveAsync(() => resolve())
      })
    })
  }

  async getItemId () {
    if (Office.context.requirements.isSetSupported('Mailbox', '1.8')) {
      // Mailbox 1.8
      return await new Promise(resolve => Office.context.mailbox.item.getItemIdAsync(result => resolve(result.value)))
    }
    throw new Error('Missing requirement set Mailbox 1.8')
  }

  mailboxVersion () {
    let version = 'unknown'

    for (let i = 1; Office.context.requirements.isSetSupported('Mailbox', '1.' + i) && i <= 20; i++) {
      version = '1.' + i
    }

    return version
  }

  platform () {
    const platform = Office.context.platform

    if (!platform) {
      return 'unknown'
    }

    return Object.keys(Office.PlatformType).find(key => Office.PlatformType[key] === platform) ?? 'unknown'
  }

  version () {
    return this.platform() + ' ' + this.mailboxVersion()
  }
}

const outlook = new Outlook()
export default outlook
